import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { waNumberFor, waBusinessMessage } from "@/lib/wa";
import { applyBulk, lineFromProduct } from "@/lib/bulkPricing";
import { useAuth } from "@/context/AuthContext";
import { printDoc, previewHtml, itemsTable, totalsBlock, quotationThermalBody, esc } from "@/lib/print";
import { isThermalFormat } from "@/lib/printFormat";
import { usePrintFormat } from "@/components/print/PrintFormatDialog";
import { toast } from "sonner";
import ManagerPinModal from "@/components/ManagerPinModal";
import CustomerPickerModal from "@/components/customers/CustomerPickerModal";
import QuotePreviewDialog from "@/components/quotes/QuotePreviewDialog";
import ProductSearchResults, { searchProducts, isExactIdentifier } from "@/components/ProductSearchResults";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileText, FilePenLine, Plus, Printer, Search, Trash2, Minus, X, ArrowRightLeft, Banknote, CreditCard, RefreshCw, AlertTriangle, ExternalLink, Percent, User, UserPlus, Eye, ShieldCheck, Truck } from "lucide-react";

const statusColor = (s) => ({
  draft: "bg-slate-500/15 text-slate-500 border-slate-500/30",
  sent: "bg-sky-500/15 text-sky-500 border-sky-500/30",
  accepted: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  converted: "bg-violet-500/15 text-violet-500 border-violet-500/30",
  expired: "bg-rose-500/15 text-rose-500 border-rose-500/30",
  cancelled: "bg-zinc-500/15 text-zinc-500 border-zinc-500/30",
}[s] || "bg-muted text-muted-foreground");

// Per-line math: percent / fixed line amount / set-price override. Mirrors POS.
const lineCalc = (i) => {
  const orig = i.price * i.quantity;
  let disc = 0, pct = 0;
  const v = parseFloat(i.discount_value || 0);
  if (i.discount_type === "percent" && v > 0) { pct = Math.min(v, 100); disc = orig * pct / 100; }
  else if (i.discount_type === "fixed" && v > 0) { disc = Math.min(v, orig); pct = orig > 0 ? disc / orig * 100 : 0; }
  else if (i.discount_type === "override" && i.discount_value !== "" && i.discount_value != null) {
    const neg = Math.min(Math.max(v, 0), i.price);
    disc = Math.max(0, i.price - neg) * i.quantity; pct = i.price > 0 ? (i.price - neg) / i.price * 100 : 0;
  }
  const roundedDisc = Math.round(disc * 100) / 100;
  return { orig, disc: roundedDisc, final: Math.round((orig - roundedDisc) * 100) / 100, pct: Math.round(pct * 100) / 100, isOverride: i.discount_type === "override" };
};

// Stage 3 — resolve a percent/fixed discount against `base` into dollars. Mirrors
// `_resolve_quote_discount` in server.py exactly (0-100% bound, fixed capped at base).
const resolveDiscount = (type, value, base) => {
  const v = parseFloat(value || 0);
  if (!(type === "percent" || type === "fixed") || v <= 0 || base <= 0) return 0;
  if (type === "percent") return Math.round(base * Math.min(Math.max(v, 0), 100) / 100 * 100) / 100;
  return Math.round(Math.min(v, base) * 100) / 100;
};

export default function Quotations() {
  const { business, user } = useAuth();
  const nav = useNavigate();
  const cur = business?.currency || "JMD $";
  const canManage = ["owner", "manager"].includes(user?.role);
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customersLoaded, setCustomersLoaded] = useState(false);
  const [products, setProducts] = useState([]);
  const [editor, setEditor] = useState(null); // quotation being created/edited
  const [convert, setConvert] = useState(null); // quotation to convert
  const [pinState, setPinState] = useState(null);
  const [prodSearch, setProdSearch] = useState("");
  const [prodDrop, setProdDrop] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [custPickerOpen, setCustPickerOpen] = useState(false);
  const [shiftRequired, setShiftRequired] = useState(false);
  const [lineDiscIdx, setLineDiscIdx] = useState(null);
  const [lineDisc, setLineDisc] = useState({ type: "percent", value: "", reason: "" });
  const [preview, setPreview] = useState(null); // { quote, isDraft }
  const [previewContact, setPreviewContact] = useState({ email: "", phone: "", name: "" }); // fetched fresh per preview open — never the lazily-loaded `customers` list, which can still be mid-fetch
  // Stage 3 correction — snapshot taken the moment the editor opens; comparing against
  // it in previewEditor() is how we detect unsaved edits so sharing can never hand out
  // a PDF/email/WhatsApp summary that differs from what the cashier is actually viewing.
  const [editorSnapshot, setEditorSnapshot] = useState(null);

  const load = () => api.get("/quotations").then((r) => setList(r.data));
  // B-06: bounded + lightweight preload (mirrors pages/POS.js's `load()`) instead of the unbounded legacy
  // GET /products — a no-op for any real tenant today (comfortably above the ~1,500-product store already
  // referenced in POS's own comments), only clipping a genuinely 8,000+ catalogue.
  const PRODUCTS_AUTO_LOAD_LIMIT = 2000;
  const mergeProducts = (rows) => { if (rows?.length) setProducts((ps) => { const m = new Map(ps.map((x) => [x.id, x])); rows.forEach((r) => m.set(r.id, { ...(m.get(r.id) || {}), ...r })); return [...m.values()]; }); };
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers((prev) => {
      // The initial GET can take seconds on a large customer list; merge rather than
      // replace so a customer created via Quick-Add in that window isn't dropped.
      const byId = new Map(r.data.map((c) => [c.id, c]));
      for (const c of prev) if (!byId.has(c.id)) byId.set(c.id, c);
      return [...byId.values()];
    })).finally(() => setCustomersLoaded(true));
    api.get("/products", { params: { limit: PRODUCTS_AUTO_LOAD_LIMIT, fields: "lite" } }).then((r) => setProducts(r.data)).catch(() => {});
  }, []);
  // Phase A-0 (same pattern as POS.js): a typed search with no local match, or a clipped local catalogue,
  // asks the indexed server search once per term; results merge in so the dropdown and Enter-to-add see them.
  const filteredProds = useMemo(() => searchProducts(products, prodSearch), [products, prodSearch]);
  const prodMissRef = useRef("");
  // B-07: sequence guard on top of the existing debounce — an out-of-order response for a term the user
  // has since typed past can no longer merge in after a newer term's own request has already resolved.
  const prodSeqRef = useRef(0);
  useEffect(() => {
    const term = prodSearch.trim();
    const clipped = products.length >= PRODUCTS_AUTO_LOAD_LIMIT;
    if (term.length < 2 || (filteredProds.length && !clipped) || prodMissRef.current === term) return;
    const mySeq = ++prodSeqRef.current;
    const t = setTimeout(() => {
      if (prodMissRef.current === term) return;   // Enter (or an earlier fire for this exact term) already resolved it
      prodMissRef.current = term;
      api.get("/products/search", { params: { q: term, limit: 40 } })
        .then((r) => { if (prodSeqRef.current === mySeq) mergeProducts(r.data); })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [prodSearch, products.length, filteredProds.length]);

  const newQuote = () => {
    const initial = {
      customer_id: "", customer_name: "Walk-in", items: [],
      discount_type: "none", discount_value: 0, discount_reason: "",
      valid_days: business?.default_quote_validity_days || 14, notes: "",
      delivery_enabled: false, delivery_fee: business?.default_delivery_fee ?? 0,
      delivery_taxable: !!business?.default_delivery_taxable, delivery_note: "",
      delivery_discount_type: "none", delivery_discount_value: 0,
      tax_exempt: false, tax_exempt_reason: "",
    };
    setEditor(initial);
    setEditorSnapshot(JSON.stringify(initial));
  };
  const editQuote = (q) => {
    const initial = {
      ...q,
      discount_type: q.discount_type || ((q.discount || 0) > 0 ? "fixed" : "none"),
      discount_value: q.discount_value ?? (q.discount || 0),
      discount_reason: q.discount_reason || "",
      delivery_enabled: (q.delivery_fee_gross ?? q.delivery_fee ?? 0) > 0,
      delivery_fee: q.delivery_fee_gross ?? q.delivery_fee ?? 0, // gross (pre-discount) fee for editing
      delivery_taxable: !!q.delivery_taxable,
      delivery_note: q.delivery_note || "",
      delivery_discount_type: q.delivery_discount_type || "none",
      delivery_discount_value: q.delivery_discount_value || 0,
      tax_exempt: !!q.tax_exempt, tax_exempt_reason: q.tax_exempt_reason || "",
    };
    setEditor(initial);
    setEditorSnapshot(JSON.stringify(initial));
  };

  const addItem = (p) => setEditor((e) => {
    const ex = e.items.find((i) => i.product_id === p.id);
    if (ex) return { ...e, items: e.items.map((i) => i.product_id === p.id ? applyBulk({ ...i, quantity: i.quantity + 1 }) : i) };
    return { ...e, items: [...e.items, applyBulk({ product_id: p.id, name: p.name + (p.variant ? ` (${p.variant})` : ""), price: p.price, sku: p.sku || "", quantity: 1, ...lineFromProduct(p), discount: 0, discount_type: "none", discount_value: 0, discount_reason: "" })] };
  });
  const setQty = (idx, q) => setEditor((e) => ({ ...e, items: e.items.map((i, n) => n === idx ? applyBulk({ ...i, quantity: Math.max(1, q) }) : i) }));
  const rm = (idx) => setEditor((e) => ({ ...e, items: e.items.filter((_, n) => n !== idx) }));

  // Stage 2: reuse the exact POS ProductSearchResults panel (full names, SKU/barcode,
  // variant/unit, price, stock, keyboard nav) instead of the old cramped name+price list.
  useEffect(() => {
    const onDown = (e) => { if (!e.target?.closest?.("[data-quote-search-root]")) setProdDrop(false); };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);
  const pickProduct = (p) => { addItem(p); setProdSearch(""); setActiveIdx(-1); setProdDrop(false); };
  // B-06: Enter always resolves against the exact current entry. A fast scanned/typed SKU or barcode that
  // isn't in the (possibly clipped) local set yet is resolved directly via the indexed server lookup —
  // mirrors pages/POS.js's addFromEntry/serverResolve so scanner-speed entry keeps working either way.
  const onProdSearchKey = async (e) => {
    const n = Math.min(filteredProds.length, 40);
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, n - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      let p = filteredProds[activeIdx >= 0 ? activeIdx : 0];
      const term = prodSearch.trim();
      if (!p && term) {
        prodMissRef.current = term;   // the debounced effect would just re-ask the same question; this already did
        try { const { data } = await api.get("/products/lookup", { params: { code: term } }); mergeProducts([data]); p = data; }
        catch { try { const { data } = await api.get("/products/search", { params: { q: term, limit: 40 } }); mergeProducts(data); p = data.find((x) => isExactIdentifier(x, term)) || data[0] || null; } catch { /* no match */ } }
      }
      if (p) pickProduct(p); else if (term) toast.error("No product found", { description: term });
    }
    else if (e.key === "Escape") { setProdDrop(false); }
  };

  // Stage 2: reuse the exact POS Quick-Add-Customer flow (search existing + create new +
  // duplicate detection) instead of a plain, non-searchable dropdown.
  const onCustomerPicked = (c) => {
    setEditor((e) => ({ ...e, customer_id: c.id, customer_name: c.name }));
    setCustomers((prev) => (prev.some((x) => x.id === c.id) ? prev : [...prev, c]));
  };
  const clearCustomer = () => setEditor((e) => ({ ...e, customer_id: "", customer_name: "Walk-in" }));

  const openLineDisc = (idx) => {
    const it = editor.items[idx];
    setLineDiscIdx(idx);
    setLineDisc(it.discount_type && it.discount_type !== "none" && it.discount_value !== "" && it.discount_value != null
      ? { type: it.discount_type, value: String(it.discount_value), reason: it.discount_reason || "" }
      : { type: "percent", value: "", reason: "" });
  };
  const saveLineDisc = () => {
    const it = editor.items[lineDiscIdx];
    const v = parseFloat(lineDisc.value || 0);
    if (lineDisc.type === "override") {
      if (!(v >= 0)) { toast.error("Enter a negotiated price"); return; }
      if (it && v > it.price) { toast.error("Negotiated price cannot exceed the regular price"); return; }
    } else {
      if (!(v > 0)) { toast.error("Enter a discount value"); return; }
      if (lineDisc.type === "percent" && v > 100) { toast.error("Percentage cannot exceed 100"); return; }
    }
    if (!lineDisc.reason.trim()) { toast.error("A reason is required"); return; }
    setEditor((e) => ({ ...e, items: e.items.map((i, n) => n === lineDiscIdx ? { ...i, discount_type: lineDisc.type, discount_value: v, discount_reason: lineDisc.reason.trim() } : i) }));
    setLineDiscIdx(null);
  };
  const clearLineDisc = (idx) => setEditor((e) => ({ ...e, items: e.items.map((i, n) => n === idx ? { ...i, discount_type: "none", discount_value: 0, discount_reason: "" } : i) }));

  // Stage 1 Fix B: mirror the backend's authoritative default exactly —
  // `.get("tax_enabled", True)` in server.py only defaults to enabled when the key is
  // MISSING (legacy business records); an explicit false (or null) still disables tax.
  // The previous `business?.tax_enabled ? … : 0` treated "missing" as disabled, which is
  // why the editor under-totalled vs. the saved/printed quotation for those businesses.
  const taxEnabled = business && "tax_enabled" in business ? !!business.tax_enabled : true;
  const taxRate = taxEnabled ? (business?.tax_rate || 0) : 0;

  // Stage 3 Pricing & Charges — whole-quote discount (percent/fixed), delivery gross
  // fee + delivery discount (percent/fixed, net fee is what's taxed/totalled, exactly
  // like `quote_totals()` on the backend), and the quote-level tax exemption. Each
  // component is rounded to cents the same way quote_totals() rounds server-side, so
  // the editor's live total can never drift a cent from what gets saved/converted.
  const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
  const eLineSubtotal = editor ? r2(editor.items.reduce((s, i) => s + lineCalc(i).final, 0)) : 0;
  const eDisc = editor ? resolveDiscount(editor.discount_type, editor.discount_value, eLineSubtotal) : 0;
  const eMerch = Math.max(0, r2(eLineSubtotal - eDisc));
  const effTaxRate = (taxEnabled && editor && !editor.tax_exempt) ? taxRate : 0;
  const eTax = r2(eMerch * effTaxRate / 100);
  const eDelGross = editor && editor.delivery_enabled ? r2(Math.max(0, parseFloat(editor.delivery_fee || 0) || 0)) : 0;
  const eDelDisc = editor && editor.delivery_enabled ? resolveDiscount(editor.delivery_discount_type, editor.delivery_discount_value, eDelGross) : 0;
  const eDelFee = Math.max(0, r2(eDelGross - eDelDisc));
  const eDelTax = editor && editor.delivery_enabled && editor.delivery_taxable ? r2(eDelFee * effTaxRate / 100) : 0;
  const eTotal = r2(eMerch + eTax + eDelFee + eDelTax);
  const isFreeDelivery = editor?.delivery_discount_type === "fixed" && eDelGross > 0 && parseFloat(editor.delivery_discount_value || 0) >= eDelGross;

  const toggleFreeDelivery = () => setEditor((e) => isFreeDelivery
    ? { ...e, delivery_discount_type: "none", delivery_discount_value: 0 }
    : { ...e, delivery_discount_type: "fixed", delivery_discount_value: Math.max(0, parseFloat(e.delivery_fee || 0) || 0) });

  const buildItems = () => editor.items.map((i) => {
    const lc = lineCalc(i);
    return { product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, sku: i.sku || "",
      discount: i.quantity > 0 ? lc.disc / i.quantity : 0,
      discount_type: i.discount_type || "none", discount_value: parseFloat(i.discount_value || 0),
      discount_reason: i.discount_reason || "" };
  });

  const saveQuote = async (ack = false) => {
    if (!editor.items.length) { toast.error("Add at least one item"); return; }
    if (editor.discount_type === "percent" && parseFloat(editor.discount_value || 0) > 100) { toast.error("Quote discount percentage must be between 0 and 100"); return; }
    if (parseFloat(editor.discount_value || 0) > 0 && !(editor.discount_reason || "").trim()) { toast.error("A reason is required for the quote discount"); return; }
    if (editor.delivery_enabled && editor.delivery_discount_type === "percent" && parseFloat(editor.delivery_discount_value || 0) > 100) { toast.error("Delivery discount percentage must be between 0 and 100"); return; }
    if (editor.tax_exempt && !(editor.tax_exempt_reason || "").trim()) { toast.error("A reason is required for the tax exemption"); return; }
    // editor.customer_name is the source of truth (set directly by onCustomerPicked / the
    // customer-select banner) — it stays correct even if a slow-loading `customers` list
    // hasn't caught up yet with a just-created customer.
    const payload = {
      customer_id: editor.customer_id, customer_name: editor.customer_id ? (editor.customer_name || "Walk-in") : "Walk-in",
      items: buildItems(), valid_days: editor.valid_days, notes: editor.notes, status: editor.status,
      discount_type: editor.discount_type || "none", discount_value: parseFloat(editor.discount_value || 0) || 0,
      discount_reason: editor.discount_reason || "",
      delivery_fee: editor.delivery_enabled ? (parseFloat(editor.delivery_fee) || 0) : 0, // gross; backend resolves the net
      delivery_taxable: !!editor.delivery_taxable, delivery_note: editor.delivery_note || "",
      delivery_discount_type: editor.delivery_enabled ? (editor.delivery_discount_type || "none") : "none",
      delivery_discount_value: editor.delivery_enabled ? (parseFloat(editor.delivery_discount_value || 0) || 0) : 0,
      tax_exempt: !!editor.tax_exempt, tax_exempt_reason: editor.tax_exempt_reason || "",
      stock_shortage_acknowledged: ack === true,
    };
    try {
      if (editor.id) await api.put(`/quotations/${editor.id}`, payload);
      else await api.post("/quotations", payload);
      toast.success("Estimate saved"); setEditor(null); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Stage 4 — editing an Accepted estimate requires an explicit revision confirm so it
      // is never silently changed; re-submit once with confirm_revision after the Cashier/
      // Owner agrees to reopen it as a new revision in Draft.
      if (detail && typeof detail === "object" && detail.needs_revision_confirm) {
        if (window.confirm(detail.message)) {
          try {
            await api.put(`/quotations/${editor.id}`, { ...payload, confirm_revision: true });
            toast.success(`Estimate saved as revision ${(detail.revision || 0) + 1} (reopened as Draft)`); setEditor(null); load();
          } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
        }
        return;
      }
      // B-32 — quote stock visibility/disclaimers: some line(s) exceed current stock on
      // hand. Acknowledging is explicit and gets RECORDED on the saved quote (never
      // bypasses the separate, hard conversion-time stock check below).
      if (detail && typeof detail === "object" && detail.stock_shortage) {
        const lines = (detail.items || []).map((it) => `• ${it.name}: need ${it.requested}, only ${it.available} on hand`).join("\n");
        if (window.confirm(`${detail.message}\n\n${lines}\n\nSave this estimate anyway?`)) {
          saveQuote(true);
        }
        return;
      }
      toast.error(apiError(detail));
    }
  };

  const del = async (id) => { await api.delete(`/quotations/${id}`); load(); };

  const setStatus = async (q, status) => {
    try { await api.put(`/quotations/${q.id}/status`, { status }); toast.success(`Estimate marked ${status}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const refreshPrices = async (q) => {
    try {
      const { data } = await api.post(`/quotations/${q.id}/refresh-prices`);
      toast.success(data.changes.length ? `${data.changes.length} price(s) updated to current` : "Prices already current");
      if (convert) setConvert(data.quote);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // Shared HTML builder for BOTH real printing and the non-printing Preview Quote
  // dialog (Stage 3) — one source of truth so the preview always matches the printout.
  const quoteDocArgs = (q, format = "standard") => {
    if (isThermalFormat(format)) {
      return { business, docTitle: "Quotation", docRef: q.quote_number || "DRAFT", format, receipt: true,
        meta: [["Customer", q.customer_name || "Walk-in"], ["Date", fmtDate(q.created_at)], ["Valid Until", fmtDate(q.valid_until)], ...(q.status && q.status !== "draft" ? [["Status", q.status.charAt(0).toUpperCase() + q.status.slice(1)]] : [])],
        body: quotationThermalBody({ ...q, valid_until_text: fmtDate(q.valid_until) }, cur, { taxLabel: business?.tax_label || "Tax", format }) };
    }
    const saleDisc = Number(q.discount || 0);
    const delivDisc = Number(q.delivery_discount || 0);
    const body = itemsTable(q.items, cur) + totalsBlock({
      subtotal: q.subtotal, tax: q.tax, total: q.total, taxLabel: business?.tax_label || "Tax",
      deliveryFee: q.delivery_fee, deliveryTax: q.delivery_tax,
      ...(saleDisc > 0.005 ? { discountAmount: saleDisc, originalSubtotal: Number(q.subtotal || 0) + saleDisc, discountLabel: "Quote Discount" } : {}),
      ...(delivDisc > 0.005 ? { deliveryDiscount: delivDisc, deliveryOriginal: Number(q.delivery_fee || 0) + delivDisc } : {}),
      ...(q.tax_exempt ? { taxExemptNote: `Tax Exempt${q.tax_exempt_reason ? " — " + q.tax_exempt_reason : ""}` } : {}),
    }, cur) + (q.delivery_note ? `<div class="notes"><strong>Delivery:</strong> ${esc(q.delivery_note)}</div>` : "")
      + (q.notes ? `<div class="notes"><strong>Notes:</strong> ${esc(q.notes)}</div>` : "");
    return { business, docTitle: "Quotation", docRef: q.quote_number || "DRAFT",
      meta: [["Customer", q.customer_name], ["Date", fmtDate(q.created_at)], ["Valid Until", fmtDate(q.valid_until)]],
      body };
  };
  const { requestPrint, dialog: printFormatDialog } = usePrintFormat("quotation", "Print Quotation");
  const printQuote = (q, format = "standard") => printDoc(quoteDocArgs(q, format));
  const previewQuoteHtml = (q, format) => previewHtml(quoteDocArgs(q, format));

  // Stage 3 — build a quote-shaped object from the live editor state so an unsaved
  // draft can be previewed/printed with the exact same math as a saved quotation.
  const draftQuoteObj = () => ({
    id: editor.id || null, quote_number: editor.quote_number || null,
    customer_id: editor.customer_id, customer_name: editor.customer_name || "Walk-in",
    items: buildItems(), subtotal: eMerch, tax: eTax, total: eTotal,
    discount: eDisc, delivery_fee: eDelFee, delivery_discount: eDelDisc, delivery_tax: eDelTax,
    delivery_taxable: !!editor.delivery_taxable, delivery_note: editor.delivery_note || "",
    tax_exempt: !!editor.tax_exempt, tax_exempt_reason: editor.tax_exempt_reason || "",
    notes: editor.notes || "", status: editor.status || "draft", created_at: editor.created_at || new Date().toISOString(),
    valid_until: editor.valid_until || new Date(Date.now() + (editor.valid_days || 14) * 86400000).toISOString().slice(0, 10),
  });

  const fetchPreviewContact = async (cid) => {
    setPreviewContact({ email: "", phone: "", name: "" });
    if (!cid) return;
    try { const { data } = await api.get(`/customers/${cid}`); setPreviewContact({ email: data?.email || "", phone: waNumberFor(data), name: data?.name || "" }); }
    catch { /* gates handle a missing/stale contact */ }
  };
  const previewEditor = () => { const q = draftQuoteObj(); setPreview({ quote: q, isDraft: !editor.id || JSON.stringify(editor) !== editorSnapshot }); fetchPreviewContact(q.customer_id); };
  const previewSaved = (q) => { setPreview({ quote: q, isDraft: false }); fetchPreviewContact(q.customer_id); };

  // Stage 3 — Preview & Share: email (reuses existing Live Customer Email Delivery
  // module) and WhatsApp Customer (free Click to Chat to the saved customer number,
  // text only — the PDF download is a separate generic action, handled inside
  // QuotePreviewDialog). Neither ever claims delivery nor changes quotation status.
  // Contact comes from `fetchPreviewContact` (a direct /customers/{id} fetch per preview
  // open), not the lazily-loaded `customers` list below, which can take several seconds
  // to arrive for a large customer base and would otherwise show a false "no number/email".
  const emailQuote = async (confirm = false) => {
    const q = preview?.quote;
    if (!q?.id) throw new Error("Save the quotation first");
    await api.post(`/quotations/${q.id}/email`, { confirm });
    load();
  };
  const whatsAppMessage = () => {
    const q = preview?.quote;
    if (!q?.id) return "";
    const name = (q.customer_name && q.customer_name !== "Walk-in") ? q.customer_name : (previewContact.name || "");
    return waBusinessMessage({
      business, kind: "quote", heading: "Quotation",
      meta: [`Reference: ${q.quote_number}`, `Date: ${fmtDate(q.created_at)}`, `Valid Until: ${fmtDate(q.valid_until)}`],
      greeting: name,
      body: [`Total: *${money(q.total)}*`],
      closing: "Please let us know if you have any questions.",
    });
  };
  const auditWhatsAppShare = async (method) => {
    const q = preview?.quote;
    if (!q?.id) return;
    try { await api.post(`/quotations/${q.id}/whatsapp-shared`, { method }); } catch { /* best-effort audit only */ }
  };
  // Stage 3 correction — Full Quotation PDF: fetches the authoritative server-rendered
  // PDF (identical totals to the saved quote / Full Page print, never recomputed on
  // the client) as a real Blob so QuotePreviewDialog can hand it to the native share
  // sheet or trigger a genuine browser download.
  const getQuotePdfBlob = async () => {
    const q = preview?.quote;
    if (!q?.id) throw new Error("Save the quotation first");
    const { data } = await api.get(`/quotations/${q.id}/pdf`, { responseType: "blob" });
    return data;
  };

  const [convertPickup, setConvertPickup] = useState(false);
  const [cashStep, setCashStep] = useState(false);
  const [cashTender, setCashTender] = useState("");
  const [convertStock, setConvertStock] = useState([]); // B-32: shortfall lines only [{name, requested, available}]

  // B-32 — stock visibility at conversion review: informational only (the real, hard
  // oversell-prevention gate is unchanged, inside _process_sale via POST /convert).
  useEffect(() => {
    setCashStep(false); setCashTender(convert ? String(convert.total || "") : ""); setConvertStock([]);
    if (!convert) return;
    const pids = (convert.items || []).map((i) => i.product_id).filter(Boolean);
    if (!pids.length) return;
    api.get(`/products?ids=${pids.join(",")}`).then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.items || []);
      const byId = {}; list.forEach((p) => { byId[p.id] = p; });
      const shortfalls = (convert.items || [])
        .filter((i) => { const p = byId[i.product_id]; return p && (p.product_type || "stock") === "stock" && p.track_inventory !== false && Number(i.quantity) > Number(p.quantity || 0); })
        .map((i) => ({ name: i.name, requested: i.quantity, available: byId[i.product_id]?.quantity || 0 }));
      setConvertStock(shortfalls);
    }).catch(() => {});
  }, [convert]);

  const cashChange = Math.max(0, (parseFloat(cashTender || 0) || 0) - (convert?.total || 0));

  const doConvert = async (mode, managerPin = null) => {
    let tendered = convert.total;
    if (mode === "cash") {
      tendered = parseFloat(cashTender || convert.total);
      if (tendered < convert.total) { toast.error("Cash tendered is less than the total."); return; }
    }
    try {
      const { data } = await api.post(`/quotations/${convert.id}/convert`, {
        mode, payment_method: mode === "card" ? "WiPay/Card" : "Cash",
        amount_tendered: mode === "credit" ? 0 : tendered, manager_pin: managerPin,
        hold_for_pickup: convertPickup && !(convert.delivery_fee > 0),
      });
      toast.success(`Converted to ${data.invoice.invoice_number}${data.invoice.pickup_reference ? ` · Pickup ${data.invoice.pickup_reference}` : ""}`);
      setConvert(null); setPinState(null); setConvertPickup(false); setCashStep(false); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.shift_required) { setConvert(null); setPinState(null); setShiftRequired(true); }
      else if (detail?.insufficient_stock) { setConvert(null); setPinState(null); toast.error("INSUFFICIENT STOCK", { description: detail.message }); }
      else if (detail?.needs_approval) { setPinState({ mode }); }
      else { toast.error(apiError(detail)); }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><FileText className="w-7 h-7 text-sky-500" /> Quotations & Estimates</h1>
          <p className="text-muted-foreground">Quote a customer, save it, print/send it, then convert to a sale. No re-entering products.</p>
        </div>
        <Button onClick={newQuote} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-quote-btn"><Plus className="w-4 h-4 mr-1.5" /> New Quotation</Button>
      </div>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Quote #</TableHead><TableHead>Customer</TableHead><TableHead>Date</TableHead><TableHead>Valid Until</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {list.map((q) => (
              <TableRow key={q.id} data-testid={`quote-row-${q.id}`}>
                <TableCell className="mono text-xs">{q.quote_number}</TableCell>
                <TableCell className="font-medium">{q.customer_name}</TableCell>
                <TableCell className="text-xs">{fmtDate(q.created_at)}</TableCell>
                <TableCell className="text-xs">{fmtDate(q.valid_until)}</TableCell>
                <TableCell className="text-right mono font-bold">{money(q.total)}</TableCell>
                <TableCell>
                  {q.status !== "converted" && q.status !== "cancelled" && !q.is_expired && !(!canManage && q.status === "accepted") ? (
                    <div className="flex items-center gap-1.5">
                      <select value={["sent", "accepted", "draft"].includes(q.status) ? q.status : "draft"}
                        onChange={(e) => setStatus(q, e.target.value)} data-testid={`quote-status-${q.id}`}
                        className="h-8 rounded-md border border-input bg-background px-2 text-xs">
                        <option value="draft">Draft</option><option value="sent">Sent</option>
                        <option value="accepted">Accepted</option>
                        {canManage && <option value="cancelled">Cancelled</option>}
                      </select>
                      {q.revision > 0 && <span className="text-[10px] text-muted-foreground whitespace-nowrap" data-testid={`quote-revision-${q.id}`}>Rev {q.revision}</span>}
                    </div>
                  ) : canManage && q.status === "cancelled" ? (
                    <select value="cancelled" onChange={(e) => setStatus(q, e.target.value)} data-testid={`quote-status-${q.id}`}
                      className="h-8 rounded-md border border-input bg-background px-2 text-xs">
                      <option value="cancelled">Cancelled</option>
                      <option value="draft">Draft</option><option value="sent">Sent</option><option value="accepted">Accepted</option>
                    </select>
                  ) : <Badge className={`capitalize ${statusColor(q.status)}`}>{q.status}{q.revision ? ` · Rev ${q.revision}` : ""}</Badge>}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => previewSaved(q)} data-testid={`preview-quote-${q.id}`} title="Preview"><Eye className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => requestPrint((format) => printQuote(q, format))} data-testid={`print-quote-${q.id}`}><Printer className="w-4 h-4" /></Button>
                    {q.status === "converted" && (
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-violet-600" data-testid={`view-invoice-${q.id}`}
                        onClick={() => q.converted_customer_id ? nav(`/customers/${q.converted_customer_id}`) : toast.message(`Sale ${q.converted_invoice_number}`, { description: "Walk-in sale. See POS sales history." })}>
                        <ExternalLink className="w-4 h-4 mr-1" />{q.converted_invoice_number || "Invoice"}
                      </Button>
                    )}
                    {q.status !== "converted" && q.status !== "cancelled" && <>
                      {q.is_expired && <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-amber-600" onClick={() => refreshPrices(q)} data-testid={`refresh-quote-${q.id}`} title="Refresh prices"><RefreshCw className="w-4 h-4" /></Button>}
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Edit" onClick={() => editQuote(q)} data-testid={`edit-quote-${q.id}`}><FilePenLine className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-emerald-600" onClick={() => setConvert(q)} data-testid={`convert-quote-${q.id}`}><ArrowRightLeft className="w-4 h-4 mr-1" />Convert</Button>
                      {canManage && <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-500" title="Delete" onClick={() => del(q.id)} data-testid={`delete-quote-${q.id}`}><Trash2 className="w-4 h-4" /></Button>}
                    </>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No quotations yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {/* Editor — Stage 2: wider, full-screen-ish workspace. Search/customer picker now
          reuse the shared POS components; pricing/discount/delivery/tax/totals math and
          every existing data-testid are unchanged from the certified Stage 1 behaviour.
          Stage 3 adds: whole-quote + delivery discounts, free delivery, a permission-gated
          tax exemption, and a dedicated Preview Quote button. */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="max-w-[1400px] w-[96vw] h-[90vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="quote-editor">
          <DialogHeader className="px-5 py-3 border-b border-border shrink-0 flex-row items-center justify-between space-y-0 pr-12">
            <div>
              <DialogTitle className="font-head">{editor?.id ? `Edit ${editor.quote_number}` : "New Quotation"}</DialogTitle>
              <DialogDescription className="sr-only">Build an estimate with products, discounts, delivery and notes.</DialogDescription>
            </div>
            {editor && (
              <Button size="sm" variant="outline" onClick={previewEditor} data-testid="quote-editor-preview-btn">
                <Eye className="w-4 h-4 mr-1.5" /> Preview Quote
              </Button>
            )}
          </DialogHeader>
          {editor && (
            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1fr_400px]">
              {/* Left: build the quote — search + line items, given the most room. */}
              <div className="min-h-0 flex flex-col border-b lg:border-b-0 lg:border-r border-border">
                <div className="p-4 pb-2 shrink-0 relative" data-quote-search-root>
                  <Label className="text-xs">Add Products</Label>
                  <div className="relative mt-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={prodSearch} onChange={(e) => { setProdSearch(e.target.value); setProdDrop(true); setActiveIdx(-1); }}
                      onFocus={() => setProdDrop(true)} onKeyDown={onProdSearchKey}
                      placeholder="Search by name, SKU or barcode…" className="pl-9" data-testid="quote-prod-search" />
                  </div>
                  {prodDrop && prodSearch.trim() && (
                    <ProductSearchResults results={filteredProds} query={prodSearch} onPick={pickProduct} testPrefix="quote-add-prod"
                      activeIndex={activeIdx} onHoverIndex={setActiveIdx} wide allowOutOfStock onDismiss={() => setProdDrop(false)} />
                  )}
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground pt-2">Line Items</p>
                  {editor.items.length === 0 && <p className="text-sm text-muted-foreground py-10 text-center">Search above to add products.</p>}
                  {editor.items.map((i, idx) => {
                    const lc = lineCalc(i);
                    const hasD = lc.disc > 0.005;
                    return (
                    <div key={idx} className="bg-secondary/50 rounded-xl p-3 space-y-2" data-testid={`quote-item-${idx}`}>
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium whitespace-normal break-words leading-snug">{i.name}</p>
                          <p className="mono text-xs text-muted-foreground mt-0.5">{i.sku ? `SKU ${i.sku} · ` : ""}{money(i.price)} each</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQty(idx, i.quantity - 1)}><Minus className="w-3 h-3" /></Button>
                          <span className="w-7 text-center mono text-sm">{i.quantity}</span>
                          <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQty(idx, i.quantity + 1)}><Plus className="w-3 h-3" /></Button>
                          <button onClick={() => rm(idx)} className="text-muted-foreground hover:text-rose-500"><X className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <button onClick={() => openLineDisc(idx)} data-testid={`quote-line-disc-btn-${idx}`}
                          className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-md px-2 py-0.5 ${hasD ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" : "text-muted-foreground hover:text-foreground border border-dashed border-border"}`}>
                          <Percent className="w-3 h-3" /> {hasD ? (lc.isOverride ? `Set Price ${money(i.discount_value)}` : `Item Discount ${lc.pct}%`) : "Discount / Set Price"}
                        </button>
                        <div className="text-right mono text-sm">
                          {hasD ? <span><span className="line-through text-muted-foreground text-xs mr-1.5">{money(lc.orig)}</span><span className="font-semibold">{money(lc.final)}</span></span>
                            : <span className="font-semibold">{money(lc.orig)}</span>}
                        </div>
                      </div>
                      {hasD && (
                        <div className="flex items-center justify-between text-[11px] text-emerald-700 dark:text-emerald-300">
                          <span>− {money(lc.disc)} · {i.discount_reason}</span>
                          <button onClick={() => clearLineDisc(idx)} className="underline hover:no-underline" data-testid={`quote-line-disc-clear-${idx}`}>remove</button>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>

              {/* Right: customer + quotation details scroll; totals + Save/Preview stay
                  pinned at the bottom (Stage 4) so they're never lost below a long form. */}
              <div className="min-h-0 flex flex-col">
                <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="p-4 space-y-3 border-b border-border">
                  <div>
                    <Label className="text-xs">Customer</Label>
                    {editor.customer_id ? (
                      <div className="mt-1 flex items-center justify-between gap-2 rounded-xl border border-border p-2.5" data-testid="quote-customer">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold truncate">{editor.customer_name || customers.find((c) => c.id === editor.customer_id)?.name}</p>
                          <p className="text-[11px] text-muted-foreground">Attached to this quotation</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setCustPickerOpen(true)} data-testid="quote-customer-change">Change</Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-muted-foreground" onClick={clearCustomer} data-testid="quote-customer-clear">Clear</Button>
                        </div>
                      </div>
                    ) : (
                      <Button variant="outline" className="w-full justify-start mt-1 border-dashed" onClick={() => setCustPickerOpen(true)} data-testid="quote-customer">
                        <User className="w-4 h-4 mr-2" /> Walk-in / No account <span className="ml-auto text-xs text-muted-foreground inline-flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" /> Select or add</span>
                      </Button>
                    )}
                  </div>

                  {/* Stage 3: delivery gross fee + optional delivery discount / free delivery. */}
                  <div className="rounded-xl border border-border p-3 space-y-2" data-testid="quote-delivery-block">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Delivery</span>
                      <Button type="button" size="sm" variant={editor.delivery_enabled ? "default" : "outline"}
                        className={`h-7 px-2.5 ${editor.delivery_enabled ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                        onClick={() => setEditor({ ...editor, delivery_enabled: !editor.delivery_enabled, delivery_fee: !editor.delivery_enabled && (!editor.delivery_fee) ? (business?.default_delivery_fee ?? 0) : editor.delivery_fee })}
                        data-testid="quote-delivery-toggle">{editor.delivery_enabled ? "Remove" : "Add Delivery"}</Button>
                    </div>
                    {editor.delivery_enabled && (
                      <div className="space-y-2" data-testid="quote-delivery-fields">
                        <div className="grid grid-cols-2 gap-2 items-end">
                          <div><Label className="text-[11px]">Fee</Label><Input type="number" min="0" value={editor.delivery_fee} onChange={(e) => setEditor({ ...editor, delivery_fee: e.target.value })} className="mono h-9" data-testid="quote-delivery-fee" /></div>
                          <label className="flex items-center gap-2 text-xs h-9" data-testid="quote-delivery-taxable-label">
                            <input type="checkbox" checked={!!editor.delivery_taxable} onChange={(e) => setEditor({ ...editor, delivery_taxable: e.target.checked })} data-testid="quote-delivery-taxable" />
                            Taxable ({business?.tax_label || "Tax"})
                          </label>
                        </div>
                        <Input value={editor.delivery_note} placeholder="Delivery note (optional)" onChange={(e) => setEditor({ ...editor, delivery_note: e.target.value })} className="h-9" data-testid="quote-delivery-note" />
                        <div className="flex items-center justify-between gap-2 pt-1">
                          <div className="flex gap-1">
                            {[["none", "No Discount"], ["percent", "% Off"], ["fixed", "$ Off"]].map(([t, lbl]) => (
                              <Button key={t} size="sm" variant={(editor.delivery_discount_type || "none") === t && !(t === "fixed" && isFreeDelivery) ? "default" : "outline"}
                                className={`h-7 px-2 text-[11px] ${(editor.delivery_discount_type || "none") === t && !(t === "fixed" && isFreeDelivery) ? "bg-sky-600 hover:bg-sky-700 text-white" : ""}`}
                                onClick={() => setEditor({ ...editor, delivery_discount_type: t, delivery_discount_value: t === "none" ? 0 : editor.delivery_discount_value })}
                                data-testid={`quote-delivery-disc-type-${t}`}>{lbl}</Button>
                            ))}
                          </div>
                          <Button size="sm" variant={isFreeDelivery ? "default" : "outline"}
                            className={`h-7 px-2 text-[11px] ${isFreeDelivery ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                            onClick={toggleFreeDelivery} data-testid="quote-delivery-free-toggle">{isFreeDelivery ? "Free ✓" : "Make Free"}</Button>
                        </div>
                        {(editor.delivery_discount_type && editor.delivery_discount_type !== "none") && (
                          <Input type="number" min="0" step="any" value={editor.delivery_discount_value}
                            placeholder={editor.delivery_discount_type === "percent" ? "e.g. 50" : "e.g. 200"}
                            onChange={(e) => setEditor({ ...editor, delivery_discount_value: e.target.value })} className="mono h-9" data-testid="quote-delivery-disc-value" />
                        )}
                        {eDelDisc > 0.005 && (
                          <div className="flex justify-between text-[11px] text-emerald-700 dark:text-emerald-300" data-testid="quote-delivery-disc-preview">
                            <span>Delivery {money(eDelGross)} − {money(eDelDisc)}</span><span className="font-semibold">{money(eDelFee)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Stage 3: quote-level tax exemption — owner/manager only, reason required, audited. */}
                  {canManage && (
                    <div className="rounded-xl border border-border p-3 space-y-2" data-testid="quote-tax-exempt-block">
                      <label className="flex items-center justify-between gap-2 cursor-pointer">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Tax Exempt</span>
                        <input type="checkbox" checked={!!editor.tax_exempt} onChange={(e) => setEditor({ ...editor, tax_exempt: e.target.checked })} data-testid="quote-tax-exempt-toggle" />
                      </label>
                      {editor.tax_exempt && (
                        <Input value={editor.tax_exempt_reason} placeholder="Exemption reason (required, audited)"
                          onChange={(e) => setEditor({ ...editor, tax_exempt_reason: e.target.value })} data-testid="quote-tax-exempt-reason" />
                      )}
                    </div>
                  )}
                  {!canManage && editor.tax_exempt && (
                    <p className="text-[11px] text-emerald-600 flex items-center gap-1" data-testid="quote-tax-exempt-readonly"><ShieldCheck className="w-3.5 h-3.5" /> Tax Exempt — {editor.tax_exempt_reason}</p>
                  )}

                  <div><Label className="text-xs">Valid For (days)</Label><Input type="number" integer min="1" value={editor.valid_days} onChange={(e) => setEditor({ ...editor, valid_days: parseInt(e.target.value || 0) })} data-testid="quote-valid" /></div>
                  <div><Label className="text-xs">Notes</Label><Textarea value={editor.notes} onChange={(e) => setEditor({ ...editor, notes: e.target.value })} data-testid="quote-notes" /></div>
                </div>
                </div>

                {/* Stage 4: sticky total + Save/Preview action area — pinned to the bottom of
                    this panel (not covering the scrollable controls above it) so the total and
                    the one Save button are always reachable without scrolling a long quote. */}
                <div className="p-4 space-y-2 shrink-0 border-t border-border bg-background" data-testid="quote-sticky-action-bar">
                  <div className="space-y-1.5" data-testid="quote-sale-discount-block">
                    <Label className="text-xs">Quote Discount</Label>
                    <div className="flex gap-1">
                      {[["none", "None"], ["percent", "% Off"], ["fixed", "$ Off"]].map(([t, lbl]) => (
                        <Button key={t} size="sm" variant={(editor.discount_type || "none") === t ? "default" : "outline"}
                          className={`h-7 px-2 text-[11px] ${(editor.discount_type || "none") === t ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                          onClick={() => setEditor({ ...editor, discount_type: t, discount_value: t === "none" ? 0 : editor.discount_value })}
                          data-testid={`quote-sale-disc-type-${t}`}>{lbl}</Button>
                      ))}
                    </div>
                    {editor.discount_type && editor.discount_type !== "none" && (
                      <>
                        <Input type="number" min="0" step="any" value={editor.discount_value}
                          placeholder={editor.discount_type === "percent" ? "e.g. 10" : "e.g. 1000"}
                          onChange={(e) => setEditor({ ...editor, discount_value: e.target.value })} className="mono h-9" data-testid="quote-sale-discount" />
                        <Input value={editor.discount_reason} placeholder="Reason (required)"
                          onChange={(e) => setEditor({ ...editor, discount_reason: e.target.value })} className="h-9" data-testid="quote-sale-discount-reason" />
                      </>
                    )}
                  </div>
                  <div className="text-sm pt-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="mono">{money(eLineSubtotal)}</span></div>
                    {eDisc > 0.005 && <div className="flex justify-between text-emerald-600"><span>Quote Discount</span><span className="mono">−{money(eDisc)}</span></div>}
                    {editor.tax_exempt ? (
                      <div className="flex justify-between text-emerald-600" data-testid="quote-tax-exempt-row"><span>Tax Exempt</span><span className="text-xs">{editor.tax_exempt_reason}</span></div>
                    ) : (taxRate > 0 && <div className="flex justify-between"><span className="text-muted-foreground">{business?.tax_label || "Tax"} ({taxRate}%)</span><span className="mono">{money(eTax)}</span></div>)}
                    {eDelGross > 0 && eDelDisc > 0.005 && <div className="flex justify-between text-muted-foreground text-xs"><span>Delivery (before discount)</span><span className="mono line-through">{money(eDelGross)}</span></div>}
                    {eDelDisc > 0.005 && <div className="flex justify-between text-emerald-600"><span>Delivery Discount</span><span className="mono">−{money(eDelDisc)}</span></div>}
                    {eDelFee > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Delivery</span><span className="mono">{money(eDelFee)}</span></div>}
                    {eDelTax > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Delivery {business?.tax_label || "Tax"}</span><span className="mono">{money(eDelTax)}</span></div>}
                    <div className="flex justify-between font-bold pt-1"><span>Total</span><span className="mono text-emerald-500" data-testid="quote-total">{money(eTotal)}</span></div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button variant="outline" onClick={previewEditor} className="shrink-0" data-testid="quote-editor-preview-btn-sticky">
                      <Eye className="w-4 h-4 mr-1.5" /> Preview
                    </Button>
                    <Button onClick={() => saveQuote(false)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-quote">Save Quotation</Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CustomerPickerModal open={custPickerOpen} onOpenChange={setCustPickerOpen} customers={customers} onSelect={onCustomerPicked}
        title="Select Customer" source="quote_quick_add" loading={!customersLoaded} transactionLabel="quote"
        onCustomerUpdated={(c) => setCustomers((prev) => prev.map((x) => (x.id === c.id ? c : x)))} />

      <QuotePreviewDialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}
        quote={preview?.quote} isDraft={!!preview?.isDraft}
        customerEmail={previewContact.email} customerPhone={previewContact.phone} customerId={preview?.quote?.customer_id}
        onCustomerUpdated={(updated) => setCustomers((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))}
        getHtml={(format) => preview ? previewQuoteHtml(preview.quote, format) : ""}
        onPrint={(format) => preview && printQuote(preview.quote, format)}
        onEmail={emailQuote} getPdfBlob={getQuotePdfBlob} waMessage={whatsAppMessage()}
        onWhatsAppShared={auditWhatsAppShare} />

      {/* Per-line discount / set price */}
      <Dialog open={lineDiscIdx !== null} onOpenChange={(o) => { if (!o) setLineDiscIdx(null); }}>
        <DialogContent className="max-w-sm" data-testid="quote-line-disc-dialog">
          <DialogHeader><DialogTitle className="font-head">Discount / Set Price</DialogTitle>
            <DialogDescription className="sr-only">Apply a percent or fixed discount, or set a negotiated price for this line.</DialogDescription>
          </DialogHeader>
          {(() => {
            const it = editor?.items?.[lineDiscIdx];
            if (!it) return null;
            const orig = it.price * it.quantity;
            const v = parseFloat(lineDisc.value || 0);
            const isOv = lineDisc.type === "override";
            const neg = isOv ? Math.min(Math.max(v, 0), it.price) : 0;
            const preview = isOv ? Math.max(0, it.price - neg) * it.quantity : (lineDisc.type === "percent" ? Math.min(Math.max(v, 0), 100) / 100 * orig : Math.min(Math.max(v, 0), orig));
            const pct = orig > 0 ? Math.round(preview / orig * 10000) / 100 : 0;
            return (
              <div className="space-y-3">
                <p className="text-sm font-medium">{it.name} <span className="text-muted-foreground">· {money(orig)} ({it.quantity} × {money(it.price)})</span></p>
                <div className="flex gap-1">
                  {[["percent", "Percent %"], ["fixed", "Fixed $"], ["override", "Set Price"]].map(([t, lbl]) => (
                    <Button key={t} size="sm" variant={lineDisc.type === t ? "default" : "outline"}
                      className={`h-8 px-2.5 ${lineDisc.type === t ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                      onClick={() => setLineDisc({ ...lineDisc, type: t, value: t === "override" ? String(it.price) : "" })} data-testid={`quote-line-disc-type-${t}`}>{lbl}</Button>
                  ))}
                </div>
                {isOv && <label className="text-[11px] text-muted-foreground">Negotiated unit price (regular {money(it.price)})</label>}
                <Input type="number" min="0" step="any" value={lineDisc.value}
                  placeholder={isOv ? "e.g. 950" : (lineDisc.type === "percent" ? "e.g. 10" : "e.g. 500")}
                  onChange={(e) => setLineDisc({ ...lineDisc, value: e.target.value })} className="mono" data-testid="quote-line-disc-value" />
                <Input value={lineDisc.reason} placeholder="Reason (required)"
                  onChange={(e) => setLineDisc({ ...lineDisc, reason: e.target.value })} data-testid="quote-line-disc-reason" />
                <div className="rounded-lg bg-secondary p-2.5 text-sm flex justify-between font-semibold">
                  <span>Line Total ({pct}% off)</span><span className="mono">{money(orig - preview)}</span>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" className="flex-1" onClick={() => setLineDiscIdx(null)} data-testid="quote-line-disc-cancel">Cancel</Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveLineDisc} data-testid="quote-line-disc-save">Apply</Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Convert */}
      <Dialog open={!!convert} onOpenChange={(o) => !o && setConvert(null)}>
        <DialogContent className="max-w-md" data-testid="convert-modal">
          <DialogHeader><DialogTitle className="font-head">Convert {convert?.quote_number}</DialogTitle>
            <DialogDescription className="sr-only">Convert this estimate to a completed sale or credit invoice.</DialogDescription>
          </DialogHeader>
          {convert?.is_expired && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3" data-testid="expired-warning">
              <p className="font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1.5 text-sm"><AlertTriangle className="w-4 h-4" /> This quotation has expired</p>
              <p className="text-xs text-muted-foreground mt-1">Product prices may have changed. Refresh with current prices before converting.</p>
              <Button size="sm" variant="outline" className="mt-2 border-amber-500/40" onClick={() => refreshPrices(convert)} data-testid="refresh-prices-btn"><RefreshCw className="w-4 h-4 mr-1.5" /> Refresh Prices</Button>
            </div>
          )}
          <p className="text-sm text-muted-foreground">Total <span className="mono font-bold text-foreground">{money(convert?.total)}</span>{convert?.delivery_fee > 0 ? ` (incl. delivery ${money(convert.delivery_fee)})` : ""}. Stocked items are checked and deducted only now, at the completed sale. Non-stock items convert even at zero stock.</p>
          {convertStock.length > 0 && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-1" data-testid="convert-stock-warning">
              <p className="font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1.5 text-sm"><AlertTriangle className="w-4 h-4" /> Stock Check</p>
              {convertStock.map((s, i) => (
                <p key={i} className="text-xs text-muted-foreground" data-testid={`convert-stock-shortage-${i}`}>{s.name}: need {s.requested}, only {s.available} on hand</p>
              ))}
              <p className="text-xs text-muted-foreground">Stock is checked again at the moment of sale and the sale will be blocked if it's still short.</p>
            </div>
          )}
          {!(convert?.delivery_fee > 0) && (
            <label className="flex items-center gap-2 text-sm rounded-lg border border-border px-3 py-2 cursor-pointer" data-testid="convert-hold-pickup-label">
              <input type="checkbox" checked={convertPickup} onChange={(e) => setConvertPickup(e.target.checked)} data-testid="convert-hold-pickup" />
              Hold for Pickup: customer collects later (goods reserved)
            </label>
          )}
          {cashStep ? (
            <div className="space-y-2 rounded-xl border border-border p-3" data-testid="convert-cash-tender-panel">
              <Label className="text-xs">Cash Tendered</Label>
              <Input type="number" min="0" step="any" value={cashTender} onChange={(e) => setCashTender(e.target.value)}
                className="mono h-11 text-base" data-testid="convert-cash-tender-input" />
              {parseFloat(cashTender || 0) >= (convert?.total || 0) && (
                <p className="text-sm">Change: <span className="mono font-bold text-emerald-500" data-testid="convert-cash-change">{money(cashChange)}</span></p>
              )}
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Button variant="outline" onClick={() => setCashStep(false)} data-testid="convert-cash-back">Back</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => doConvert("cash")} data-testid="convert-cash-confirm">Confirm Cash Sale</Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 mt-2">
              <Button className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white justify-start" onClick={() => { setCashTender(String(convert.total || "")); setCashStep(true); }} data-testid="convert-cash"><Banknote className="w-4 h-4 mr-2" /> Cash Sale</Button>
              <Button className="h-12 justify-start" variant="outline" onClick={() => doConvert("card")} data-testid="convert-card"><CreditCard className="w-4 h-4 mr-2" /> Card / WiPay Sale</Button>
              <Button className="h-12 justify-start border-violet-500/40 text-violet-600 dark:text-violet-400" variant="outline" disabled={!convert?.customer_id} onClick={() => doConvert("credit")} data-testid="convert-credit"><CreditCard className="w-4 h-4 mr-2" /> Customer Credit Invoice</Button>
              {!convert?.customer_id && <p className="text-xs text-muted-foreground">Attach a customer to the quote to charge on credit.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {printFormatDialog}
      <ManagerPinModal open={!!pinState} onClose={() => setPinState(null)} title="Credit Approval Required"
        reason="This credit conversion needs manager authorization."
        onSubmit={async (pin) => { await doConvert(pinState.mode, pin); }} />

      <Dialog open={shiftRequired} onOpenChange={setShiftRequired}>
        <DialogContent className="max-w-sm" data-testid="quote-shift-required-modal">
          <DialogHeader><DialogTitle className="font-head">SHIFT REQUIRED</DialogTitle>
            <DialogDescription>A cashier shift must be open before converting a quotation into a sale.</DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5" /></div>
            <p className="text-sm text-muted-foreground">You must open a cashier shift before completing this conversion into a sale.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button variant="outline" onClick={() => setShiftRequired(false)} data-testid="quote-shift-cancel">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setShiftRequired(false); nav("/shift"); }} data-testid="quote-shift-open">
              <ArrowRightLeft className="w-4 h-4 mr-2" /> OPEN SHIFT
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
